import { type FC, useState, useEffect, useCallback } from 'react';
import { createRefetchContainer, graphql, type RelayRefetchProp } from 'react-relay/legacy.js';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { TRADE } from 'dibs-buyer-layout/exports/userTypeEnums.ts';
import { addLoginHandlers } from 'dibs-buyer-layout/src/utils/loginHandlers.ts';
import { trackPageview, trackUserEventHomepageView } from 'dibs-tracking';
import { mobile } from 'dibs-client-check';
import { useIntl } from 'dibs-react-intl';
import { useHomePageContext } from './HomePageContext.tsx';
import getLazyLoadStartingPoint from './helpers/getLazyLoadStartingPoint.js';
import { HpSharedAppBannerLazy } from './HpSharedAppBanner/HpSharedAppBannerLazy.tsx';
import { HpSharedCompleteEmailOptinConfirmationModalLazy } from './HpSharedCompleteEmailOptinConfirmationModal/HpSharedCompleteEmailOptinConfirmationModalLazy.tsx';
import {
    getUserSessionCountryCode,
    getUserZipCode,
} from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { useDwelledOnPage } from 'dibs-buyer-layout/exports/dwelledOnPage.ts';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { componentModuleMap } from './componentModuleMap.tsx';
import { HpSharedBillboardAd } from './HpSharedBillboardAd/HpSharedBillboardAd.tsx';
import {
    type DeviceType,
    MOBILE,
    TABLET,
    DESKTOP,
} from 'dibs-recent-history/exports/RecentlyViewedItemsProvider.ts';
import { HpSharedRecentlyViewedItemsModule } from './HpSharedRecentlyViewedItems/HpSharedRecentlyViewedItemsModule.tsx';
import { HpRecentlySearchedItemsModuleLazy } from './HpRecentlySearchedItems/HpRecentlySearchedItemsModuleLazy.tsx';
import {
    HOME_BOTTOM_BILLBOARD_PLACEMENT,
    HOME_TOP_BILLBOARD_PLACEMENT,
} from 'dibs-google-ad-manager/exports/constants.ts';

import { type HpSharedLayout_viewer$data } from './__generated__/HpSharedLayout_viewer.graphql.ts';
import HpSharedSkinnyBannerModule from './HpSharedSkinnyBannerModule/HpSharedSkinnyBannerModule.tsx';
import { HpSharedFeaturedModule } from './HpSharedFeaturedModule/HpSharedFeaturedModule.tsx';
import { HpSharedHeroModule } from './HpSharedHeroModule/HpSharedHeroModule.tsx';
import { HpSharedNewArrivalsCarouselTabs } from './HpSharedNewArrivalsCarouselTabs/HpSharedNewArrivalsCarouselTabs.tsx';
import { isRemoveShopByTestVariant } from './helpers/removeShopByTestHelpers.ts';
import { trackRemoveTopHeroBannerTest } from './helpers/removeTopHeroBannerTestHelpers.ts';
import { trackRemoveCuratedContentTest } from './helpers/removeCuratedContentTestHelpers.ts';
import { HpPersonalizedCollectionBanners } from './HpPersonalizedCollectionBanners/HpPersonalizedCollectionBanners.tsx';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { HpSharedFeaturedItems } from './HpSharedFeaturedItems/HpSharedFeaturedItems.tsx';
import { trackHpMixedGridMvpTest } from './helpers/hpMixedGridMvpTestHelpers.ts';
import { HpSharedUserRecommendations } from './HpSharedUserRecommendations/HpSharedUserRecommendations.tsx';
import { trackExpand2TowerRecsTest } from './helpers/expand2TowerRecsTestHelpers.ts';
import {
    getUserRecommendationType,
    trackSiglipRecsTest,
} from 'dibs-personalized/exports/siglipRecsTestHelpers.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';
import { HpSharedItemsGrid } from './HpSharedItemsGrid/HpSharedItemsGrid.tsx';

const HpSharedRecentlyFavoritedCarouselLazy = dibsLazy(
    'HpSharedRecentlyFavoritedCarousel',
    () =>
        import(
            /* webpackChunkName: "HpSharedRecentlyFavoritedCarousel" */ './HpSharedRecentlyFavoritedCarousel/HpSharedRecentlyFavoritedCarousel.tsx'
        )
);

type HpSharedLayoutProps = {
    relay: RelayRefetchProp;
    viewer: HpSharedLayout_viewer$data;
};

const HpSharedLayoutComponent: FC<HpSharedLayoutProps> = ({ relay, viewer }) => {
    const intl = useIntl();
    const [userId, setUserId] = useState<string | null>(null);
    const [userType, setUserType] = useState<string | null>(null);
    const [isClient, setIsClient] = useState<boolean>(false);
    const [fetchFavorites, setFetchFavorites] = useState(false);

    const {
        isMobile,
        isTablet,
        isLoggedInUser,
        isTopDisplayAdEnabled,
        isBottomDisplayAdEnabled,
        isEvergreenCollectionsTestVariant,
        isHpMixedGridTestVariant,
        isRemoveCuratedContentTestVariant,
        isBot,
        userType: userTypeFromSever,
    } = useHomePageContext();

    let deviceType: DeviceType = DESKTOP;
    if (isMobile) {
        deviceType = MOBILE;
    } else if (isTablet) {
        deviceType = TABLET;
    }

    const user = viewer?.user || null;

    const doRefetch = useCallback(
        (additionalVariables = {}) => {
            relay.refetch(
                fragmentVariables => ({
                    ...fragmentVariables,
                    fetchInitialHomepage: false,
                    isClient: true,
                    isMobile,
                    hasUserId: !!userId,
                    userId: userId || '',
                    userIds: userId ? [userId] : [],
                    userType: userType || userTypeFromSever || '',
                    zipCode: getUserZipCode(),
                    isTrade: userType === TRADE,
                    userCountryCode: getUserSessionCountryCode(),
                    shouldSkipShopByModule:
                        isRemoveShopByTestVariant() || !!isRemoveCuratedContentTestVariant,
                    isRemoveCuratedContentTestVariant: !!isRemoveCuratedContentTestVariant,
                    isHpMixedGridTestVariant,
                    recommendationType: getUserRecommendationType(),
                    ...additionalVariables,
                }),
                { fetchInitialHomepage: true },
                () => {
                    setIsClient(true);

                    // ensure that we are done with refetch before considering to allow favorites fetch
                    if (userId) {
                        setFetchFavorites(true);
                    }
                }
            );
        },
        [
            userId,
            userType,
            userTypeFromSever,
            relay,
            isMobile,
            isHpMixedGridTestVariant,
            isRemoveCuratedContentTestVariant,
        ]
    );

    const setUserData = useCallback(() => {
        const cookieUserId = getBuyerId(document.cookie);

        if (cookieUserId && !userId) {
            // order is important to prevent unnecessary refetch, read more: https://github.com/1stdibs/ferrum/pull/13601#pullrequestreview-818832289
            setUserType(getUserType(document.cookie));
            setUserId(cookieUserId);
        }
    }, [userId]);

    useEffect(() => {
        trackRemoveTopHeroBannerTest();
    }, []);

    useEffect(() => {
        trackPageview({ additional: { opt_pageURL: '/' } });
    }, []);

    useEffect(() => {
        trackUserEventHomepageView(relay.environment);
    }, [relay.environment]);

    useEffect(() => {
        if (!isLoggedInUser && !isClient) {
            doRefetch();
        }
    }, [doRefetch, isLoggedInUser, isClient]);

    useEffect(() => {
        if (userId && !user) {
            doRefetch();
        }
    }, [doRefetch, userId, user]);

    useEffect(() => {
        setUserData();
        addLoginHandlers((data: { user: { isVerifiedTrade?: boolean | null } }) => {
            if (data?.user?.isVerifiedTrade) {
                // keep this to make sure a trade member
                // logging in on the hp is "redirected"
                // to the trade homepage
                window.location.reload();
                return;
            }
            setUserData();
        });
    }, [setUserData, intl.locale]);

    useDwelledOnPage(PAGE_TYPE.HOME);

    const { homepage } = viewer;

    const isUser = isLoggedInUser || !!userId; // isLoggedInUser comes at load; userId might come later from refetch

    const { personalizationCollections, modules } = homepage || {};

    const promoPrimaryModule = (modules || []).find(
        mod => mod && mod.type === 'promo' && mod.index === 0
    );
    const promoPrimaryItemsCount = (promoPrimaryModule?.messages?.items || []).length;
    const startLazyLoadAt = getLazyLoadStartingPoint({ isMobile, isTablet });
    const hasLoggedInWithApp = user?.hasLoggedInWithApp;

    let editorialIndex = 1;

    const mods = (modules || []).map((mod, index) => {
        if (!mod) {
            return null;
        }
        const type = mod.type;

        if (!type) {
            return null;
        }

        const component = componentModuleMap[type];
        if (component) {
            return component({
                componentModule: mod,
                key: `${type}-${index}`,
                startingPromoIndex: type === 'promo' && mod.index ? promoPrimaryItemsCount : 0,
                viewer,
                isMobile,
                useLazyLoadImages: index >= startLazyLoadAt,
                moduleIndex: index,
                totalModules: modules?.length || 0,
                user,
                isClient,
                isEvergreenCollectionsTestVariant: !!isEvergreenCollectionsTestVariant,
                isHpMixedGridTestVariant: !!isHpMixedGridTestVariant,
                isRemoveCuratedContentTestVariant: !!isRemoveCuratedContentTestVariant,
                editorialModuleOrder: type === 'editorial' ? editorialIndex++ : 0,
            });
        }
        return null;
    });

    const getModuleIndex = ({
        moduleKey,
        direction = 'forward',
    }: {
        moduleKey: string;
        direction?: 'forward' | 'backward';
    }): number => {
        const moduleIndex =
            direction === 'forward'
                ? mods.findIndex(mod => (mod?.key || '').toString().includes(moduleKey))
                : mods.findLastIndex(mod => (mod?.key || '').toString().includes(moduleKey));

        if (moduleIndex === -1) {
            return mods.length - 1;
        }

        return moduleIndex;
    };

    const isOneEditorialModule =
        getModuleIndex({ moduleKey: 'editorial' }) ===
        getModuleIndex({ moduleKey: 'editorial', direction: 'backward' });

    /**
     * non cms controlled modules insertion
     */
    mods.splice(
        0,
        0,
        <HpSharedRecentlyViewedItemsModule
            key="hp-recentlyViewed"
            moduleIndex={1}
            totalModules={modules?.length || 0}
            deviceType={deviceType}
        />
    );

    const curatedContentAbTestTracker = (
        <VisibilityTracker
            key="hp-remove-curated-content-ab-test-tracker"
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackRemoveCuratedContentTest();
                }
            }}
        />
    );

    mods.splice(getModuleIndex({ moduleKey: 'editorial' }) + 1, 0, curatedContentAbTestTracker);

    if (isUser) {
        mods.splice(
            getModuleIndex({ moduleKey: 'editorial' }) + 1,
            0,
            <HpSharedNewArrivalsCarouselTabs
                key="hp-new-arrivals-carousel-tabs"
                viewer={viewer}
                user={user || null}
                personalizationCollections={personalizationCollections?.filter(filterFalsy) || null}
            />
        );
    }

    const hpMixedGridMvpTestTracker = (
        <VisibilityTracker
            key="hp-mixed-grid-mvp-ab-test-tracker"
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackHpMixedGridMvpTest();
                }
            }}
        />
    );

    if (!isHpMixedGridTestVariant) {
        mods.splice(
            isUser
                ? getModuleIndex({ moduleKey: 'hp-new-arrivals-carousel-tabs' }) + 1
                : getModuleIndex({ moduleKey: 'editorial' }) + 1,
            0,
            hpMixedGridMvpTestTracker,
            <HpSharedHeroModule
                key="hp-editors-picks"
                viewer={viewer}
                user={user}
                isMobile={isMobile}
                isClient={isClient}
            />
        );
    }

    let recentlySearchedInsertIndex: number;
    if (isHpMixedGridTestVariant) {
        recentlySearchedInsertIndex = isUser
            ? getModuleIndex({ moduleKey: 'hp-new-arrivals-carousel-tabs' }) + 1
            : getModuleIndex({ moduleKey: 'editorial' }) + 1;
    } else {
        recentlySearchedInsertIndex = getModuleIndex({ moduleKey: 'hp-editors-picks' });
    }
    mods.splice(
        recentlySearchedInsertIndex,
        0,
        <HpRecentlySearchedItemsModuleLazy key="hp-recentlySearched" />
    );

    if (isHpMixedGridTestVariant) {
        mods.splice(
            getModuleIndex({ moduleKey: 'hp-recentlySearched' }) + 1,
            0,
            hpMixedGridMvpTestTracker,
            <HpSharedItemsGrid
                key={`hp-shared-items-grid${userId ? `-${userId}` : ''}`}
                userId={userId}
                fetchFavorites={fetchFavorites}
            />
        );
    }

    mods.splice(
        getModuleIndex({ moduleKey: 'locations' }) + 1,
        0,
        <HpSharedFeaturedModule
            key="featured"
            viewer={isClient ? viewer : null}
            user={user}
            isClient={isClient}
        />
    );

    const expand2TowerRecsAbTestTracker = (
        <VisibilityTracker
            key="hp-expand-2tower-recs-ab-test-tracker"
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackExpand2TowerRecsTest();
                }
            }}
        />
    );

    const siglipRecsAbTestTracker = (
        <VisibilityTracker
            key="hp-siglip-recs-ab-test-tracker"
            onVisibilityChange={({ isVisible }) => {
                if (isVisible) {
                    trackSiglipRecsTest();
                }
            }}
        />
    );

    mods.splice(
        getModuleIndex({ moduleKey: 'editorial' }) + 1,
        0,
        expand2TowerRecsAbTestTracker,
        siglipRecsAbTestTracker,
        <HpSharedUserRecommendations key="hp-user-recommendations" user={user} />
    );
    if (!isHpMixedGridTestVariant) {
        mods.splice(
            getModuleIndex({ moduleKey: 'editorial' }) + 1,
            0,
            <HpPersonalizedCollectionBanners
                key="hp-personalized-catalogues-banner-0"
                bannerIndex={0}
            />
        );
        mods.splice(
            getModuleIndex({ moduleKey: 'hp-editors-picks' }) + 1,
            0,
            <HpPersonalizedCollectionBanners
                key="hp-personalized-catalogues-banner-1"
                bannerIndex={1}
            />
        );
        mods.splice(
            getModuleIndex({ moduleKey: 'locations' }) + 1,
            0,
            <HpPersonalizedCollectionBanners
                key="hp-personalized-catalogues-banner-2"
                bannerIndex={2}
            />
        );
        mods.splice(
            getModuleIndex({ moduleKey: 'featured' }) + 1,
            0,
            <HpPersonalizedCollectionBanners
                key="hp-personalized-catalogues-banner-3"
                bannerIndex={3}
            />
        );
    }

    if (isClient) {
        mods.splice(
            getModuleIndex({ moduleKey: 'featured' }) + 2,
            0,
            <HpSharedRecentlyFavoritedCarouselLazy
                key="hp-recently-saved"
                userId={userId || ''}
                isTrade={userType === TRADE}
                priceBookName={''}
            />
        );
    }

    if (isClient) {
        // always insert after the recently saved module. recently saved test has complete and rolled out just not cleaned up
        mods.splice(
            getModuleIndex({ moduleKey: 'hp-recently-saved' }),
            0,
            <VisibilityTracker
                key="hp-remove-curated-content-ab-test-tracker"
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackRemoveCuratedContentTest();
                    }
                }}
            />
        );
    }

    if (isOneEditorialModule) {
        mods.splice(
            getModuleIndex({ moduleKey: 'shopBy' }),
            0,
            <HpSharedSkinnyBannerModule key="skinnyBanner" />
        );
    } else {
        mods.splice(
            getModuleIndex({ moduleKey: 'editorial', direction: 'backward' }),
            0,
            <HpSharedSkinnyBannerModule key="skinnyBanner" />
        );
    }

    if (isTopDisplayAdEnabled) {
        mods.splice(
            1, // insert after the recently viewed items module
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-top"
                placement={HOME_TOP_BILLBOARD_PLACEMENT}
            />
        );
    }

    if (isBottomDisplayAdEnabled) {
        mods.splice(
            getModuleIndex({ moduleKey: 'locations' }) + 1,
            0,
            <HpSharedBillboardAd
                key="billboardAd-home-bottom"
                placement={HOME_BOTTOM_BILLBOARD_PLACEMENT}
            />
        );
    }

    if (!isBot && !isHpMixedGridTestVariant) {
        mods.splice(
            getModuleIndex({ moduleKey: 'locations' }) + 1,
            0,
            <HpSharedFeaturedItems
                key={`featured-items${userId ? `-${userId}` : ''}`}
                userId={userId}
                isTrade={userType === TRADE}
            />
        );
    }

    return (
        <>
            {mods}
            {isClient && <HpSharedCompleteEmailOptinConfirmationModalLazy />}
            {isClient &&
                (mobile.iPhone() || mobile.iPad()) &&
                !(isLoggedInUser && hasLoggedInWithApp) && <HpSharedAppBannerLazy user={user} />}
        </>
    );
};

export const HpSharedLayout = createRefetchContainer(
    HpSharedLayoutComponent,
    {
        viewer: graphql`
            fragment HpSharedLayout_viewer on Viewer
            @argumentDefinitions(
                recommendationType: { type: "UserRecommendationType!", defaultValue: PERSONALIZED }
            ) {
                homepage(rootId: $homepageId, abTestList: $abTestList) {
                    modules {
                        ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                            # don't skip this module on refetch, otherwise the dibs-carousel within
                            # won't get any items
                            ... on TopHeroBannerModule {
                                type
                                ...HpSharedTopHeroBannerModule_componentModule
                            }
                        }
                        ... on CollectionsModule
                            @include(if: $fetchInitialHomepage)
                            @skip(if: $isRemoveCuratedContentTestVariant) {
                            type
                            ...HpSharedCollectionsModule_componentModule
                        }
                        ... on PromoModule @skip(if: $isRemoveCuratedContentTestVariant) {
                            index
                            type
                            messages(userType: $userType) {
                                items {
                                    __typename
                                }
                            }
                            ...HpSharedPromoModule_componentModule
                        }
                        ... on EditorialModule @include(if: $fetchInitialHomepage) {
                            type
                            ...HpSharedEditorialModule_componentModule
                        }
                        ... @skip(if: $shouldSkipShopByModule) {
                            ... on ShopByModule @include(if: $isClient) {
                                type
                                ...HpSharedShopByModule_componentModule
                            }
                        }
                        ... on LocationsModule {
                            type
                        }
                        ... on BottomBannerModule {
                            type
                            # visible used in componentModuleMap to check if the module is visible
                            # eslint-disable-next-line relay/unused-fields
                            visible
                            ...HpSharedFeaturedBannerModule_componentModule
                        }
                        ... on VideoModule
                            @include(if: $isClient)
                            @skip(if: $isRemoveCuratedContentTestVariant) {
                            type
                            ...HpSharedVideoComponent_componentModule
                        }
                    }
                    personalizationCollections(userId: $userId, numItems: 10, pageSize: 3)
                        @include(if: $hasUserId) {
                        ...HpSharedNewArrivalsCarouselTabs_personalizationCollections
                    }
                }
                ...HpSharedHeroModule_viewer @skip(if: $isHpMixedGridTestVariant)
                ... @skip(if: $isHpMixedGridTestVariant) {
                    ... @include(if: $isEvergreenCollectionsTestVariant) {
                        ...HpSharedTrendingNowCarouselTabs_viewer @include(if: $isClient)
                    }
                    ... @skip(if: $isEvergreenCollectionsTestVariant) {
                        ...HpSharedLocationsModule_viewer @include(if: $isClient)
                    }
                }
                ...HpSharedNewArrivalsTabContent_viewer
                ...HpSharedFeaturedModule_viewer @include(if: $isClient)
                ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                    ...HpSharedTopHeroBannerModule_viewer
                }
                ...HpSharedVideoComponent_viewer
                    @include(if: $isClient)
                    @skip(if: $isRemoveCuratedContentTestVariant)
                user(userId: $userId) @include(if: $hasUserId) {
                    hasLoggedInWithApp: hasLoggedInWithDeviceType(deviceType: IOS_APP)
                    ...HpSharedHeroModule_user @skip(if: $isHpMixedGridTestVariant)
                    ... @skip(if: $isHpMixedGridTestVariant) {
                        ...HpSharedTrendingNowCarouselTabs_user
                            @include(if: $isEvergreenCollectionsTestVariant)
                        ...HpSharedLocationsModule_user
                            @skip(if: $isEvergreenCollectionsTestVariant)
                    }
                    ...HpSharedNewArrivalsTabContent_user
                    ...HpSharedVideoComponent_user
                        @include(if: $isClient)
                        @skip(if: $isRemoveCuratedContentTestVariant)
                    ...HpSharedFeaturedModule_user
                    ...HpSharedAppBanner_user
                    ... @skip(if: $isRemoveTopHeroBannerTestVariant) {
                        ...HpSharedTopHeroBannerModule_user
                    }
                    ...HpSharedUserRecommendations_user
                        @arguments(recommendationType: $recommendationType)
                }
            }
        `,
    },
    graphql`
        query HpSharedLayoutRefetchQuery(
            $hasUserId: Boolean!
            $userId: String!
            $homepageId: String!
            $isMobile: Boolean!
            $fetchInitialHomepage: Boolean!
            $userType: String!
            $abTestList: [String] = []
            $userCountryCode: String!
            $isTrade: Boolean!
            $priceBookName: String!
            $isClient: Boolean!
            $shouldSkipShopByModule: Boolean!
            $isEvergreenCollectionsTestVariant: Boolean!
            $isRemoveTopHeroBannerTestVariant: Boolean!
            $isHpMixedGridTestVariant: Boolean!
            $isRemoveCuratedContentTestVariant: Boolean!
            $recommendationType: UserRecommendationType!
        ) {
            viewer {
                ...HpSharedLayout_viewer @arguments(recommendationType: $recommendationType)
            }
        }
    `
);
